import { useState } from 'react';
import { ArrowPathIcon, MagnifyingGlassIcon, FunnelIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectCompanyAdherentsBodacc, 
  selectBodaccLoading, 
  selectBodaccTotal, 
  setBodaccPage, 
  selectBodaccPage, 
  selectBodaccLimit
} from '../store/slices/bodaccSlice';
import { BODACC_FAMILY_TYPES, formatDate, getBodaccBorderColorClass } from '../utils/bodaccUtils';
import { getBodaccFieldLabel } from '../utils/bodaccDictionary';
import BodaccDetail from './BodaccDetail';
import NotesButton from './notes/NotesButton';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import { Link } from 'react-router-dom';

const BodaccTable = () => {
  const dispatch = useDispatch();
  const bodaccData = useSelector(selectCompanyAdherentsBodacc);
  const isLoading = useSelector(selectBodaccLoading);
  const total = useSelector(selectBodaccTotal);
  const [selectedSiren, setSelectedSiren] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const currentPage = useSelector(selectBodaccPage);
  const itemsPerPage = useSelector(selectBodaccLimit);

  const handlePageChange = (page: number) => {
    dispatch(setBodaccPage(page) as any);
  };

  const handleOpenDetail = (siren: string) => {
    setSelectedSiren(siren);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedSiren(null);
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showSuccessToast('SIREN copié dans le presse-papier', 'siren-copied');
      })
      .catch((err) => {
        showErrorToast('Erreur lors de la copie du SIREN', 'siren-copy-error');
        console.error('Erreur lors de la copie :', err);
      });
  };

  const openGoogleSearch = (searchTerm: string) => {
    const encodedSearch = encodeURIComponent(searchTerm);
    window.open(`https://www.google.com/search?q=${encodedSearch}`, '_blank');
  };

  // Function to render adherent information
  const renderAdherents = (adherents?: any[]) => {
    if (!adherents || !Array.isArray(adherents) || adherents.length === 0) return null;
    
    return (
      <div className="text-xs text-gray-500 mt-1">
        Contact : {adherents.map((adherent, index) => (
          <span key={adherent.id}>
            <Link 
              to={`/adherents/${adherent.id}`}
              target="_blank"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {adherent.contactName || 'Contact non spécifié'}
            </Link>
            {adherent.contactEmail && ` (${adherent.contactEmail})`}
            {index < adherents.length - 1 ? ', ' : ''}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                {getBodaccFieldLabel('siren')}
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                {getBodaccFieldLabel('dateparution')}
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                <div className="flex items-center">
                  {getBodaccFieldLabel('familleavis')}
                  <FunnelIcon className="h-4 w-4 ml-1 text-indigo-500 hidden group-hover:inline-block" aria-hidden="true" />
                </div>
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                {getBodaccFieldLabel('typeavis_lib')}
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                {getBodaccFieldLabel('publicationavis')}
              </th>
              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-10 text-center">
                  <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                </td>
              </tr>
            ) : bodaccData.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <FunnelIcon className="h-8 w-8 text-gray-400 mb-2" />
                    <p>Aucune publication BODACC trouvée pour vos adhérents</p>
                    <p className="text-xs mt-1">Essayez de modifier vos critères de recherche ou de sélectionner d'autres types d'avis</p>
                  </div>
                </td>
              </tr>
            ) : (
              bodaccData.map((item) => {
                const familyType = item.publication.familleavis;
                const familyLabel = BODACC_FAMILY_TYPES[familyType as keyof typeof BODACC_FAMILY_TYPES] || familyType;
                const borderColorClass = getBodaccBorderColorClass(familyType);
                
                return (
                  <tr key={item.bodacc_id} className="hover:bg-gray-50">
                    <td className="py-4 pl-4 pr-3 text-sm sm:pl-6">
                      <div className="flex items-center space-x-2">
                        <div className="font-medium text-gray-900">
                          {item.publication.commercant || 'Entreprise non spécifiée'}
                        </div>
                        <NotesButton
                          siren={item.siren}
                          companyName={item.publication.commercant || 'Entreprise'}
                          variant="icon"
                          size="sm"
                        />
                      </div>
                      <div className="flex items-center text-xs text-gray-600 mt-1">
                        <span>SIREN: {item.siren}</span>
                        <button 
                          onClick={() => copyToClipboard(item.siren)}
                          className="ml-2 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title="Copier le SIREN"
                        >
                          <ClipboardIcon className="h-4 w-4 text-gray-500" />
                        </button>
                        <button 
                          onClick={() => openGoogleSearch(item.publication.commercant || item.siren)}
                          className="ml-1 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title="Rechercher sur Google"
                        >
                          <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                      {renderAdherents(item.adherents)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatDate(item.publication.dateparution)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${borderColorClass.replace('border', 'bg').replace('-4', '/10')} ${borderColorClass.replace('border', 'text').replace('-4', '/800')}`}>
                        {familyLabel}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {item.publication.typeavis_lib || 'Jugement'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {item.publication.publicationavis}/N°{item.publication.numeroannonce}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <button
                        onClick={() => handleOpenDetail(item.siren)}
                        className="text-indigo-600 hover:text-indigo-900 flex items-center"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
                        Détails
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                currentPage === 1 ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Précédent
            </button>
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
                currentPage === totalPages ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> à{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, total)}</span> sur{' '}
                <span className="font-medium">{total}</span> résultats
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                    currentPage === 1 
                      ? 'text-gray-300' 
                      : 'text-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Précédent</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        currentPage === pageNumber
                          ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                    currentPage === totalPages 
                      ? 'text-gray-300' 
                      : 'text-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Suivant</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {selectedSiren && (
        <BodaccDetail
          isOpen={isDetailOpen}
          onClose={handleCloseDetail}
          siren={selectedSiren}
        />
      )}
    </div>
  );
};

export default BodaccTable; 